import { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ShortcutsHelpModalProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutEntry {
  keys: string;
  description: string;
}

interface ShortcutSection {
  title: string;
  items: ShortcutEntry[];
}

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const SHORTCUT_SECTIONS: ShortcutSection[] = [
  {
    title: 'Global',
    items: [
      { keys: '\u2318K', description: 'Command Palette' },
      { keys: '\u2318S', description: 'Save' },
      { keys: '\u2318Z', description: 'Undo' },
      { keys: '\u2318\u21e7Z', description: 'Redo' },
      { keys: 'Esc', description: 'Close / Deselect' },
      { keys: '?', description: 'This help' },
    ],
  },
  {
    title: 'Book Editor',
    items: [
      { keys: '\u2318\u21e7N', description: 'New Chapter' },
      { keys: '\u2318E', description: 'Metadata Panel' },
      { keys: '\u2318\u21e7P', description: 'Preview' },
      { keys: '\u2318R', description: 'Image Generation' },
      { keys: '\u2318D', description: 'Dictionary' },
      { keys: '\u2318/', description: 'Slash Menu' },
      { keys: '\u2318\u21e7F', description: 'Focus Mode' },
    ],
  },
  {
    title: 'Navigation',
    items: [{ keys: '\u23181-6', description: 'Switch engine tab' }],
  },
  {
    title: 'AI',
    items: [
      { keys: '\u2318/', description: 'Slash commands' },
      { keys: 'Right-click', description: 'Context menu' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function ShortcutsHelpModal({ open, onClose }: ShortcutsHelpModalProps) {
  const [filter, setFilter] = useState('');

  const filteredSections = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return SHORTCUT_SECTIONS;

    return SHORTCUT_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          item.description.toLowerCase().includes(q) ||
          item.keys.toLowerCase().includes(q),
      ),
    })).filter((section) => section.items.length > 0);
  }, [filter]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="span">
          Keyboard Shortcuts
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search shortcuts..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2 }}
        />

        {filteredSections.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: 'center' }}
          >
            No shortcuts match your search.
          </Typography>
        )}

        <Stack spacing={3}>
          {filteredSections.map((section) => (
            <Box key={section.title}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  mb: 1,
                  display: 'block',
                }}
              >
                {section.title}
              </Typography>

              <Stack spacing={0.5}>
                {section.items.map((item) => (
                  <Box
                    key={item.keys + item.description}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.75,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography variant="body2">{item.description}</Typography>
                    <Chip
                      label={item.keys}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        height: 26,
                        minWidth: 48,
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
